import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, User, LayoutDashboard, Search, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/Header.css';

// Force refresh utility - clears PWA cache (exported for use in Footer)
export const forceRefreshApp = () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'FORCE_REFRESH' });
    // Also clear browser storage
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    // Reload after a brief delay
    setTimeout(() => {
      window.location.reload(true);
    }, 500);
  } else {
    // No service worker, just hard reload
    window.location.reload(true);
  }
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const searchRef = useRef(null);
  const headerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  
  // Check if user is admin/staff
  const isAdminUser = user && ['admin', 'staff', 'reviewer', 'provider'].includes(user.role);

  // Searchable pages and sections
  const searchableItems = [
    { label: 'Home', path: '/', keywords: ['home', 'welcome', 'start', 'main'] },
    { label: 'The CADENCE Mission', path: '/about-us', keywords: ['about', 'team', 'founders', 'mission', 'lakia', 'andera', 'who we are', 'why cadence exists'] },
    { label: 'CADENCE Promise', path: '/cadence-promise', keywords: ['services', 'diagnostic', 'advocacy', 'deep dive', 'consultation', 'help', 'promise', 'what we do'] },
    { label: 'Why We Exist', path: '/why-we-exist', keywords: ['why', 'exist', 'disparities', 'healthcare', 'mission'] },
    { label: 'Pricing', path: '/pricing', keywords: ['pricing', 'price', 'cost', 'packages', 'rates', 'how much', 'fees'] },
    { label: 'Membership Plans', path: '/membership', keywords: ['membership', 'member', 'subscribe', 'subscription', 'plans', 'navigator', 'partner', 'advocate'] },
    { label: 'CADENCE Compass', path: '/articles', keywords: ['compass', 'articles', 'guides', 'resources', 'blog', 'learn', 'education'] },
    { label: 'Deep Dives - Hirsutism', path: '/articles', keywords: ['hirsutism', 'hair', 'deep dive', 'inflammation', 'pathway', 'root cause'] },
    { label: 'Interactive Guides', path: '/articles', keywords: ['interactive', 'guides', 'slideshow', 'learn'] },
    { label: 'Contact Us', path: '/contact', keywords: ['contact', 'email', 'phone', 'reach', 'schedule', 'appointment', 'book'] },
    { label: 'Patient Portal', path: '/portal', keywords: ['portal', 'dashboard', 'login', 'account', 'patient'] },
    { label: 'Health Intake Form', path: '/portal', keywords: ['intake', 'form', 'health', 'questionnaire', 'submit'] },
    { label: 'Affiliate Program', path: '/portal?tab=affiliate', keywords: ['affiliate', 'referral', 'partner', 'commission', 'earn'] },
    { label: 'Terms of Service', path: '/terms', keywords: ['terms', 'service', 'legal', 'agreement'] },
    { label: 'Privacy & Refund Policy', path: '/privacy', keywords: ['privacy', 'refund', 'policy', 'data'] },
    { label: 'HIPAA Compliance', path: '/hipaa', keywords: ['hipaa', 'compliance', 'security', 'protected', 'health'] },
    { label: 'Lupus Resources', path: '/articles?category=lupus', keywords: ['lupus', 'autoimmune', 'chronic'] },
    { label: 'Thyroid Health', path: '/articles?category=thyroid', keywords: ['thyroid', 'hashimoto', 'hormone'] },
    { label: 'Veterans Care', path: '/articles?category=veterans', keywords: ['veteran', 'military', 'va', 'service'] },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-hide header after 3 seconds, show on hover
  useEffect(() => {
    // Clear any existing timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }

    // Start hide timer (3 seconds)
    hideTimerRef.current = setTimeout(() => {
      if (!isHovering && !isMobileMenuOpen && !searchOpen) {
        setIsHeaderVisible(false);
      }
    }, 3000);

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [isHovering, isMobileMenuOpen, searchOpen]);

  // Handle mouse enter/leave for header
  const handleMouseEnter = () => {
    setIsHovering(true);
    setIsHeaderVisible(true);
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    // Start hide timer when mouse leaves
    hideTimerRef.current = setTimeout(() => {
      if (!isMobileMenuOpen && !searchOpen) {
        setIsHeaderVisible(false);
      }
    }, 2000);
  };

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const results = searchableItems.filter(item => 
      item.label.toLowerCase().includes(lowerQuery) ||
      item.keywords.some(kw => kw.includes(lowerQuery))
    ).slice(0, 6);

    setSearchResults(results);
  };

  // Navigate to result
  const handleResultClick = (path) => {
    navigate(path);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    window.scrollTo(0, 0);
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleResultClick(searchResults[0].path);
    } else if (e.key === 'Escape') {
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  // Nav links - "Landing" (Coming Soon page) only visible to admin users
  const baseNavLinks = [
    { path: '/home', label: 'Home' },
    { path: '/founders', label: 'Founders' },
    { path: '/about-us', label: 'The CADENCE Mission' },
    { path: '/cadence-promise', label: 'CADENCE Promise' },
    { path: '/why-we-exist', label: 'Why We Exist' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/membership', label: 'Membership' },
    { path: '/articles', label: 'CADENCE Compass' }
  ];
  
  // Add Landing (Coming Soon) as first item for admin users
  const navLinks = isAdminUser 
    ? [{ path: '/welcome', label: 'Landing' }, ...baseNavLinks]
    : baseNavLinks;

  // Hide header on portal page
  if (location.pathname.startsWith('/portal')) {
    return null;
  }

  return (
    <>
      {/* Hover trigger zone at top of screen when header is hidden */}
      {!isHeaderVisible && (
        <div 
          className="header-hover-zone"
          onMouseEnter={handleMouseEnter}
        />
      )}
      <header 
        className={`header ${isScrolled ? 'scrolled' : ''} ${!isHeaderVisible ? 'header-hidden' : ''}`}
        ref={headerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
      <div className="container">
        <div className="header-content">
          {/* Big C Emblem - Clickable for Menu on Mobile/Tablet, Link to Home on Desktop */}
          <div className="logo-menu-wrapper">
            <button 
              className="logo-menu-trigger"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="emblem-menu-toggle"
            >
              <img src="/cadence-foil-logo.png" alt="CADENCE" className="c-emblem" />
            </button>
            
            {/* Dropdown Menu from Emblem */}
            {isMobileMenuOpen && (
              <div className="emblem-dropdown-menu" data-testid="emblem-dropdown">
                <button 
                  className="dropdown-close"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X size={20} />
                </button>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`dropdown-link ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="dropdown-divider" />
                {isAuthenticated ? (
                  <>
                    <Link 
                      to="/portal" 
                      className="dropdown-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Portal
                    </Link>
                    <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="dropdown-link">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link 
                    to="/login" 
                    className="dropdown-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Patient Portal
                  </Link>
                )}
                <Link 
                  to="/contact" 
                  className="dropdown-cta"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          <nav className="nav-menu desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => window.scrollTo(0, 0)}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="header-actions">
            {/* Refresh Button - Clears PWA Cache */}
            <button 
              className="refresh-btn"
              onClick={forceRefreshApp}
              aria-label="Refresh page and clear cache"
              title="Clear cache & refresh"
              data-testid="header-refresh-btn"
            >
              <RefreshCw size={18} />
            </button>

            {/* Global Search - Hidden on mobile to prevent blocking emblem */}
            <div className={`search-wrapper ${searchOpen ? 'open' : ''}`} ref={searchRef}>
              <button 
                className="search-toggle"
                onClick={() => setSearchOpen(!searchOpen)}
                aria-label="Search"
              >
                <Search size={20} />
              </button>
              
              {searchOpen && (
                <div className="search-dropdown">
                  <div className="search-input-wrapper">
                    <Search size={18} />
                    <input
                      type="text"
                      placeholder="Search pages..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      data-testid="global-search-input"
                    />
                  </div>
                  
                  {searchResults.length > 0 && (
                    <ul className="search-results">
                      {searchResults.map((result, idx) => (
                        <li key={idx}>
                          <button onClick={() => handleResultClick(result.path)}>
                            {result.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {searchQuery.length >= 2 && searchResults.length === 0 && (
                    <div className="search-no-results">
                      No results found for "{searchQuery}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <>
                <Link to="/portal" className="btn-portal" data-testid="header-portal-btn">
                  <LayoutDashboard size={18} />
                  <span>Portal</span>
                </Link>
                <button onClick={logout} className="btn-login">
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-login" data-testid="header-login-btn">
                  <User size={18} />
                  <span>Patient Portal</span>
                </Link>
              </>
            )}
            <Link to="/contact" className="btn-primary" data-testid="header-get-started">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;